import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import toast from "components/partials/toast/toast";

import { getCampaignFolder } from "generated/api/campaign-folder";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useAddTouchpointsFromMondayTemplateContext } from "components/modals/add-touchpoints-from-template-modal/context/add-touchpoints-from-monday-template-context";
import { folderKeys, touchpointKeys } from "hooks/api/query-keys";

import type { AddTouchpointsFromMondayTemplateFormData } from "components/modals/add-touchpoints-from-template-modal/add-touchpoints-from-template-modal.types";

export const useCreateFoldersFromTemplate = () => {
  const { campaignTemplates, setIsOpen, onSuccess } = useAddTouchpointsFromMondayTemplateContext();
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const queryClient = useQueryClient();

  const createFolders = useCallback(
    async (formData: AddTouchpointsFromMondayTemplateFormData) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let requestCompleted = false;

      try {
        const clientId = currentClient.id;
        const campaignId = currentCampaign.id;
        if (!clientId || !campaignId) {
          throw new Error("Client ID or Campaign ID is missing");
        }

        const selectedCampaignTemplateIds = formData.campaignTemplates || [];
        const selectedTouchpointIds = formData.templates || {};

        // Build campaign templates array for the batch request
        const campaignTemplateItems = selectedCampaignTemplateIds
          .map((campaignTemplateId) => {
            const campaignTemplate = campaignTemplates.find((ct) => ct.id === campaignTemplateId);
            if (!campaignTemplate) return null;

            const selectedTouchpoints = (campaignTemplate.touchpointTemplates || []).filter(
              (tp) => tp.id && selectedTouchpointIds[tp.id] === true,
            );

            if (!selectedTouchpoints.length) return null;

            return {
              campaignTemplateId,
              campaignTemplateName: campaignTemplate.campaignTemplate || "",
              touchpointTemplates: selectedTouchpoints,
            };
          })
          .filter((item) => item !== null);

        if (!campaignTemplateItems.length) {
          toast.error({
            data: {
              title: "No creatives selected",
              message: "Please select at least one creative to create.",
            },
          });
          return;
        }

        const totalRequested = campaignTemplateItems.reduce(
          (sum, item) => sum + item.touchpointTemplates.length,
          0,
        );

        // Set a timer to show "operation in progress" message if request takes too long
        timeoutId = setTimeout(() => {
          if (!requestCompleted) {
            toast.info({
              data: {
                title: "Operation in progress",
                message:
                  "The operation is still processing. Please check back in a couple of minutes.",
              },
              autoClose: false,
            });
            setIsOpen(false);
            onSuccess();
            requestCompleted = true;
          }
        }, 119000); // 1 minute 59 seconds

        const campaignFolder = getCampaignFolder();
        const folders = await campaignFolder.createFoldersFromMondayTemplates(
          { clientId, campaignId },
          { campaignTemplates: campaignTemplateItems },
        );

        if (timeoutId) clearTimeout(timeoutId);
        requestCompleted = true;

        queryClient.invalidateQueries({ queryKey: folderKeys.all });
        queryClient.invalidateQueries({ queryKey: touchpointKeys.all });

        toast.success({
          data: {
            title: `Added ${totalRequested} creative${totalRequested > 1 ? "s" : ""} to ${folders.length} folder${folders.length > 1 ? "s" : ""}`,
          },
        });
        setIsOpen(false);
        onSuccess();
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);

        // Only show error if we haven't already shown the "in progress" message
        if (!requestCompleted) {
          console.error("Failed to create folders from templates:", error);
          toast.error({
            data: {
              title: "Failed to create folders",
            },
          });
        }
      }
    },
    [campaignTemplates, currentClient, currentCampaign.id, setIsOpen, onSuccess, queryClient],
  );

  return { createFolders };
};
